'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Bath, BedDouble, ImageOff, LayoutGrid, List, MapPin, Maximize2 } from 'lucide-react';
import { StickyFilterBar } from '@/components/listings/StickyFilterBar';
import { Pagination } from '@/components/ui/pagination';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { PropertyCard, type PropertyCardData } from '@/components/PropertyCard';
import { cn, formatPrice, getPropertyTypeLabel, BLUR_DATA_URL } from '@/lib/utils';

type ViewMode = 'grid' | 'list';

interface ListingsClientProps {
  properties: PropertyCardData[];
  total: number;
  page: number;
  totalPages: number;
  basePath: string;
  hasActiveFilters: boolean;
  transactionType: 'sale' | 'rent';
  filterValues: {
    q?: string;
    type?: string;
    citySlug?: string;
    minPrice?: string;
    maxPrice?: string;
    rooms?: string;
    bedrooms?: string;
    minSurface?: string;
    maxSurface?: string;
    sort?: string;
    order?: string;
    isFeatured?: string;
  };
}

export function ListingsClient({
  properties,
  total,
  page,
  totalPages,
  basePath,
  hasActiveFilters,
  transactionType,
  filterValues,
}: ListingsClientProps) {
  function buildHref(p: number) {
    const sp = new URLSearchParams();
    Object.entries(filterValues).forEach(([k, v]) => {
      if (v) sp.set(k, v);
    });
    if (p > 1) sp.set('page', String(p));
    const qs = sp.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  }
  const [view, setView] = useState<ViewMode>('grid');

  return (
    <>
      {/* ── Sticky filter bar ── */}
      <StickyFilterBar
        key={JSON.stringify(filterValues)}
        transactionType={transactionType}
        basePath={basePath}
        filterValues={filterValues}
      />

      <section className="bg-surface">
      <div className="container-page py-6 md:py-8">
        {/* ── Toolbar ── */}
        <div className="flex items-center gap-3 mb-5">
          {/* View toggle */}
          <div className="flex items-center gap-0.5 rounded-xl border border-border-subtle bg-surface-cream p-1">
            <button
              type="button"
              onClick={() => setView('grid')}
              aria-label="Vue grille"
              aria-pressed={view === 'grid'}
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors duration-200',
                view === 'grid' ? 'bg-surface shadow-sm text-ink' : 'text-ink-muted hover:text-ink',
              )}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-label="Vue liste"
              aria-pressed={view === 'list'}
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors duration-200',
                view === 'list' ? 'bg-surface shadow-sm text-ink' : 'text-ink-muted hover:text-ink',
              )}
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {/* Result count */}
          <p className="text-sm text-ink-secondary shrink-0 ml-auto">
            <strong className="font-display text-xl text-ink mr-1">
              {total.toLocaleString('fr-MA')}
            </strong>
            {total === 1 ? 'annonce' : 'annonces'}
          </p>
        </div>

        {/* ── Results ── */}
        {properties.length > 0 ? (
          <>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                {properties.map((property, i) => (
                  <PropertyCard key={property.id} property={property} priority={i < 4} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-border-subtle rounded-2xl border border-border-subtle overflow-hidden">
                {properties.map((property, i) => (
                  <ListRow key={property.id} property={property} priority={i < 4} />
                ))}
              </div>
            )}
            <Pagination
              page={page}
              totalPages={totalPages}
              buildHref={buildHref}
              className="mt-12"
            />
          </>
        ) : (
          <EmptyState
            icon={<MapPin className="h-8 w-8 text-accent" aria-hidden />}
            title={
              hasActiveFilters
                ? 'Aucun bien ne correspond à votre recherche'
                : 'Aucune annonce disponible pour le moment'
            }
            description={
              hasActiveFilters
                ? 'Élargissez vos critères ou supprimez certains filtres pour voir plus de résultats.'
                : 'Revenez bientôt — de nouvelles annonces sont publiées chaque jour.'
            }
            action={
              hasActiveFilters ? (
                <Button asChild variant="outline">
                  <Link href={basePath}>Réinitialiser les filtres</Link>
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
    </section>
    </>
  );
}

// ── List row ─────────────────────────────────────────────────────────────────

function ListRow({ property, priority }: { property: PropertyCardData; priority: boolean }) {
  const isRent = property.transactionType === 'rent';
  const thumb = property.media?.[0]?.thumbnailUrl || property.media?.[0]?.url;

  return (
    <Link
      href={`/annonce/${property.slug}`}
      className="group flex gap-4 sm:gap-6 p-4 sm:p-5 bg-surface hover:bg-surface-cream transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
    >
      {/* Thumbnail */}
      <div className="relative w-32 sm:w-44 h-24 sm:h-32 shrink-0 overflow-hidden rounded-xl bg-surface-cream">
        {thumb ? (
          <Image
            src={thumb}
            alt={property.title}
            fill
            sizes="(max-width: 640px) 128px, 176px"
            priority={priority}
            placeholder="blur"
            blurDataURL={BLUR_DATA_URL}
            className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-ink-muted">
            <ImageOff className="h-6 w-6 opacity-40" />
          </div>
        )}
        <span
          className={cn(
            'absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide',
            isRent ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground',
          )}
        >
          {isRent ? 'Location' : 'Vente'}
        </span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 py-1 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-eyebrow text-accent mb-1">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {property.district?.name
                ? `${property.district.name}, ${property.city?.name ?? ''}`
                : property.city?.name || 'Maroc'}
            </span>
          </div>
          <h3 className="font-display text-base sm:text-lg text-ink leading-snug line-clamp-2 group-hover:text-accent transition-colors duration-200">
            {property.title}
          </h3>
          <p className="mt-0.5 text-[11px] uppercase tracking-eyebrow text-ink-muted">
            {getPropertyTypeLabel(property.type)}
          </p>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-ink-secondary">
          {property.bedrooms > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <BedDouble className="h-3.5 w-3.5 shrink-0" />
              {property.bedrooms} ch.
            </span>
          )}
          {property.bathrooms > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Bath className="h-3.5 w-3.5 shrink-0" />
              {property.bathrooms} sdb
            </span>
          )}
          {(property.surface ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Maximize2 className="h-3.5 w-3.5 shrink-0" />
              {property.surface} m²
            </span>
          )}
        </div>
      </div>

      {/* Price */}
      <div className="hidden sm:flex flex-col items-end justify-center shrink-0 pl-2">
        <p className="font-display text-xl text-ink whitespace-nowrap">
          {formatPrice(property.price)}
        </p>
        {isRent && <span className="text-xs text-ink-muted">/mois</span>}
        {property.isFeatured && (
          <span className="mt-2 text-[10px] uppercase tracking-wide text-accent font-semibold">
            ★ Sélection
          </span>
        )}
      </div>
    </Link>
  );
}
